// src/context/signalREncorehive.js
import React, { createContext, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { HubConnectionBuilder } from "@microsoft/signalr";
import { selectCurrentUser } from "../features/auth/authSlice";

// Create a Context for SignalR Encorehive
const SignalREncorehiveContext = createContext();

// Provider component to manage SignalR connection
export const SignalREncorehiveProvider = ({ children }) => {
  const user = useSelector(selectCurrentUser); // Select the current user from Redux store
  const [connection, setConnection] = useState(null); // State to hold the SignalR connection

  // Construct the SignalR hub URL from environment variables
  const hubURL = (window.ENV?.VITE_ENCOREHIVE_REALTIME_SERVER_URL || import.meta.env.VITE_ENCOREHIVE_REALTIME_SERVER_URL) + "/encorehiveHub";
  console.log("signalREncorehive Hub URL:", hubURL); // Log the hub URL

  useEffect(() => {
    // Exit early if user is not logged in or connection already exists
    if (!user || connection) {
      return;
    }

    // Initialize a new SignalR connection
    const newConnection = new HubConnectionBuilder()
      .withUrl(hubURL)
      .withAutomaticReconnect() // Enable automatic reconnection
      .configureLogging("information")
      .build();

    // Function to start the SignalR connection
    const startConnection = async () => {
      try {
        await newConnection.start();
        console.log("signalREncorehive Connected to encorehiveHub");
        setConnection(newConnection);
      } catch (err) {
        console.error("signalREncorehive Error connecting to encorehiveHub:", err);
        setTimeout(startConnection, 5000);
      }
    };

    // Handle connection closure and attempt to reconnect
    newConnection.onclose(async () => {
      console.log("signalREncorehive Connection closed, attempting to reconnect to encorehiveHub..."); // Log connection closure
      await startConnection();
    });

    startConnection(); // Initiate the connection

    // Cleanup function to stop the connection when the component unmounts
    return () => {
      if (newConnection) {
        newConnection.stop();
        console.log("signalREncorehive Connection to encorehiveHub stopped, component unmounted"); // Log connection stop
      }
    };
  }, [user, connection, hubURL]);

  // Context data to be provided to consuming components
  const contextData = {
    signalREncorehive: connection,
  };

  return (
    <SignalREncorehiveContext.Provider value={contextData}>
      {children} {/* Render child components */}
    </SignalREncorehiveContext.Provider>
  );
};

export default SignalREncorehiveContext;
